import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useInput } from "react-admin";
import AsyncSelect from "react-select/async";
import axios from "axios";
import { apiUrl } from "../../dataProvider";
import { FormHelperText } from "@material-ui/core";
import "./style.css";

const RESOURCE = "builder-platform";
const PAGE_SIZE = 1000;
const DEBOUNCE_DELAY = 150;

const DEFAULT_OPTION = { id: null, store_name: "Default", name: "Default", isDefault: true };

const getOptionLabel = (e) => {
  if (!e || e.isDefault) return "Default";
  return e.store_name || e.name || `Platform ${e.id}`;
};


const CustomBuilderPlatformSelect = ({
  source = "builder_platform_name",
  idsource = "builder_platform_id",
  validate = [],
  pageSize = PAGE_SIZE,
  isDisabled = false,
  isEdit = false,
  onSelectionChange,
  ...props
}) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const hasLoadedInitialList = useRef(false);
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  const {
    input,
    meta: { touched, error },
  } = useInput({ source, validate });
  const { input: inputSourceId } = useInput({ source: idsource });

  const getLists = useCallback((params) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    return axios
      .get(`${apiUrl}/${RESOURCE}`, { 
        params,
        signal: abortControllerRef.current.signal 
      })
      .then(({ data }) => data.data)
      .catch((error) => {
        if (error.name !== 'CanceledError') {
          throw error;
        }
      });
  }, []);

  const getSearchOptions = useCallback((value) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    const searchTerm = value?.trim();
    if (!searchTerm) {
      return Promise.resolve([DEFAULT_OPTION, ...list]);
    }
    
    return new Promise((resolve) => {
      debounceTimerRef.current = setTimeout(() => {
        const lowerSearch = searchTerm.toLowerCase();
        const filtered = list.filter((item) => {
          const storeName = (item.store_name || "").toLowerCase();
          const name = (item.name || "").toLowerCase();
          return storeName.includes(lowerSearch) || name.includes(lowerSearch);
        });
        resolve([DEFAULT_OPTION, ...filtered]);
      }, DEBOUNCE_DELAY);
    });
  }, [list]);

  const setStoreList = useCallback(
    async (params) => {
      if (loading) return;
      
      try {
        setLoading(true);
        const response = await getLists(params);
        if (isEdit) {
          const selected = response.find(
            (item) =>
              item.id === inputSourceId.value ||
              item.store_name === input.value ||
              item.name === input.value
          );
          if (selected) {
            input.onChange(selected.store_name || selected.name);
            inputSourceId.onChange(selected.id);
          }
        }
        setList(response);
        hasLoadedInitialList.current = true;
      } catch (error) {
        if (error.name !== 'CanceledError') {
          setList([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [isEdit, input, inputSourceId, getLists, loading]
  );

  useEffect(() => {
    if (!hasLoadedInitialList.current && list.length === 0) {
      setStoreList({
        "page[number]": 1,
        "page[size]": pageSize,
        sort: "store_name",
      });
    }
  }, [setStoreList, pageSize]);

  const handleChange = useCallback((callback) => (selectedopt) => {
    const newId = selectedopt?.isDefault ? null : (selectedopt?.id ?? null);
    const newName = selectedopt?.isDefault ? null : (selectedopt?.store_name || selectedopt?.name || null);

    // Update form fields first
    if (idsource) {
      inputSourceId.onChange(newId);
      callback(newName);
    } else {
      callback(newId);
    }

    // Call onSelectionChange callback (this triggers parent's API call)
    if (onSelectionChange) {
      onSelectionChange(newId);
    }
  }, [idsource, inputSourceId, onSelectionChange]);

  const selectedValue = useMemo(() => {
    if (inputSourceId.value == null || inputSourceId.value === "") {
      return DEFAULT_OPTION;
    }
    return list.find((item) => item.id === inputSourceId.value) || DEFAULT_OPTION;
  }, [inputSourceId.value, list]);

  const options = useMemo(() => [DEFAULT_OPTION, ...list], [list]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <div>
      <AsyncSelect
        {...input}
        value={selectedValue}
        isClearable
        cacheOptions
        isLoading={loading}
        styles={{
          menu: (provided) => ({ ...provided, zIndex: 9999 }),
        }}
        defaultOptions={options}
        isDisabled={isDisabled}
        getOptionLabel={getOptionLabel}
        getOptionValue={(e) => (e?.isDefault ? "" : e?.id)}
        loadOptions={getSearchOptions}
        onChange={handleChange(input.onChange)}
        placeholder="Search or select builder platform..."
      />
      {touched && error && (
        <FormHelperText error>
          {error === "ra.validation.required" ? "Required" : error}
        </FormHelperText>
      )}
    </div>
  );
};

export default CustomBuilderPlatformSelect;
